/**
 * Helpers for the "Plan my week" page (D29): default start date, time
 * formatting in the household's zone, and turning the proposal plus the
 * user's swaps/locks/skips into the next request or the accept body.
 */
import type {
  PlannerAcceptRequestInput,
  PlannerDayDto,
  PlannerWeekRequest,
} from '@fewerbags/schema';
import { addDays, parseIsoDate } from './planDates';

export type PlannerLock = PlannerWeekRequest['locked'][string];

/** Next Monday after `today` (Monday itself when today is a Monday). */
export function defaultPlanStart(today: string): string {
  const d = parseIsoDate(today);
  if (Number.isNaN(d.getTime())) return today;
  const ahead = (8 - d.getDay()) % 7; // 0 on Monday
  return addDays(today, ahead);
}

/** "18:30" → "6:30 PM". */
export function formatClock(hhmm: string): string {
  const m = /^(\d{2}):(\d{2})/.exec(hhmm);
  if (!m) return hhmm;
  const h = Number(m[1]);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${suffix}`;
}

/** An ISO instant as clock time in the household's zone ("5:30 PM"). */
export function formatInstant(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d
      .toLocaleTimeString('en-US', { timeZone, hour: 'numeric', minute: '2-digit' })
      .replace(/\u202f/g, ' ');
  } catch {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
}

/** "25 min", "1 h 10 min". */
export function formatMinutes(min: number | null): string | null {
  if (min === null) return null;
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const rest = min % 60;
  return rest ? `${h} h ${rest} min` : `${h} h`;
}

/** Days the user can add: a main is proposed and the day isn't planned or skipped. */
export function acceptableDays(days: readonly PlannerDayDto[]): PlannerDayDto[] {
  return days.filter((d) => d.main !== null && d.skipped === null && d.existingPlan === null);
}

/** The current picks of a day as a lock. */
export function lockFor(day: PlannerDayDto): PlannerLock {
  return {
    mainRecipeId: day.main?.recipe.recipeId ?? null,
    sideRecipeIds: day.sides.map((s) => s.recipe.recipeId),
  };
}

/** The v2 per-meal key: `YYYY-MM-DD|slot` (D33). */
export function mealKey(day: Pick<PlannerDayDto, 'date' | 'slot'>): string {
  return `${day.date}|${day.slot}`;
}

/**
 * Pin every other proposed day to its current picks, so swapping one day
 * doesn't reshuffle the rest of the week (the planner is greedy by date).
 * `keyOf` names a meal: the bare date (D29 page, one slot) or `mealKey`
 * (auto-plan, several slots a day). Only cooked picks are pinned; dine-out
 * and leftovers meals follow from the request and their source meal.
 */
export function pinsExcept(
  days: readonly PlannerDayDto[],
  key: string,
  keyOf: (day: PlannerDayDto) => string = (d) => d.date,
): Record<string, PlannerLock> {
  const pins: Record<string, PlannerLock> = {};
  for (const d of acceptableDays(days)) {
    const k = keyOf(d);
    if (k !== key) pins[k] = lockFor(d);
  }
  return pins;
}

/** `exclude` with `recipeId` added for `date` (no duplicates). */
export function withExcluded(
  exclude: Record<string, string[]>,
  date: string,
  recipeId: string,
): Record<string, string[]> {
  const current = exclude[date] ?? [];
  if (current.includes(recipeId)) return exclude;
  return { ...exclude, [date]: [...current, recipeId] };
}

/**
 * Meals the auto-plan wizard can add (D33): a cooked or packed pick, a
 * dine-out night, or packed leftovers of an earlier meal — never one that is
 * planned already or skipped.
 */
export function acceptableMeals(days: readonly PlannerDayDto[]): PlannerDayDto[] {
  return days.filter(
    (d) =>
      d.skipped === null &&
      d.existingPlan === null &&
      (d.main !== null || d.kind === 'dine_out' || d.leftoversOf !== null),
  );
}

/** v2 accept options: the wizard knows guests by name, the proposal only counts them. */
export interface AcceptV2Options {
  guestNames?: (day: PlannerDayDto) => readonly string[];
}

type AcceptDay = PlannerAcceptRequestInput['days'][number];

function acceptDayV2(d: PlannerDayDto, opts: AcceptV2Options): AcceptDay {
  const dineOut = d.kind === 'dine_out';
  const leftovers = d.leftoversOf !== null && d.main === null;
  const recipeIds =
    dineOut || leftovers || !d.main
      ? []
      : [d.main.recipe.recipeId, ...d.sides.map((s) => s.recipe.recipeId)];
  const out: AcceptDay = {
    date: d.date,
    slot: d.slot,
    serveAt: d.serveAt,
    recipeIds,
    kind: d.kind,
    placeId: d.place?.id ?? null,
    estCostCents: d.cost?.totalCents ?? null,
  };
  if (d.servings !== null && d.servings >= 1) out.servings = Math.min(50, d.servings);
  if (dineOut) out.dineOutNote = d.dineOut?.note ?? null;
  if (leftovers) out.leftoversOf = { date: d.leftoversOf!.date, slot: d.leftoversOf!.slot };
  if (d.attendees && !d.attendees.everyone) {
    const named = [...(opts.guestNames?.(d) ?? [])].slice(0, d.attendees.guests);
    while (named.length < d.attendees.guests) named.push(`Guest ${named.length + 1}`);
    out.attendees = { personIds: d.attendees.personIds, guests: named };
  }
  return out;
}

/**
 * The accept request for the days that have a proposal. With `v2` (the
 * auto-plan wizard) it also carries kind, place, attendees, servings, the
 * cost estimate, dine-out notes and packed leftovers; without it the D29
 * body is unchanged.
 */
export function acceptBody(
  days: readonly PlannerDayDto[],
  groceryList: { create: boolean; storeId?: string | null } | null,
  v2?: AcceptV2Options,
): PlannerAcceptRequestInput {
  return {
    days: v2
      ? acceptableMeals(days).map((d) => acceptDayV2(d, v2))
      : acceptableDays(days).map((d) => ({
          date: d.date,
          slot: d.slot,
          serveAt: d.serveAt,
          recipeIds: [d.main!.recipe.recipeId, ...d.sides.map((s) => s.recipe.recipeId)],
        })),
    ...(groceryList?.create
      ? {
          groceryList: {
            create: true,
            ...(groceryList.storeId ? { storeId: groceryList.storeId } : {}),
          },
        }
      : {}),
  };
}
